/* global Excel */

import { ArticleResult, EnrichmentField, LookupType } from "../api/types";

/**
 * Writes scalar enrichment values to the worksheet using batch range operations.
 * Groups writes by column and sets all values in a single range write per column,
 * avoiding cell-by-cell operations for much better performance with large datasets.
 */
export async function writeEnrichmentData(
  articles: ArticleResult[],
  fieldColumnMapping: Record<string, string>,
  articleRowMap: Map<string, number>,
  noChangeIfEmpty: boolean,
  lookupType: LookupType = "ArticleCode"
): Promise<void> {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();

      // Determine the contiguous row range from the articleRowMap
      const rows = Array.from(articleRowMap.values());
      if (rows.length === 0) return;
      const minRow = Math.min(...rows);
      const maxRow = Math.max(...rows);
      const rowCount = maxRow - minRow + 1;

      // Build a lookup: code → index within the row range
      const articleByRow = new Map<number, ArticleResult>();
      for (const article of articles) {
        const key = lookupType === "Barcode" ? article.barcode : article.articleCode;
        if (!key) continue;
        const row = articleRowMap.get(key);
        if (row !== undefined) {
          articleByRow.set(row, article);
        }
      }

      // For each field/column, read existing values then write the full column in one batch
      for (const [field, col] of Object.entries(fieldColumnMapping)) {
        if (field === EnrichmentField.ThumbnailBinaryAsBase64) continue;

        const rangeAddress = `${col}${minRow}:${col}${maxRow}`;
        const range = sheet.getRange(rangeAddress);

        if (noChangeIfEmpty) {
          // Need existing values to preserve cells where we have no data
          range.load("values");
        }
      }

      if (noChangeIfEmpty) {
        await context.sync();
      }

      // Now build and write the batch values for each column
      for (const [field, col] of Object.entries(fieldColumnMapping)) {
        if (field === EnrichmentField.ThumbnailBinaryAsBase64) continue;

        const rangeAddress = `${col}${minRow}:${col}${maxRow}`;
        const range = sheet.getRange(rangeAddress);

        // Build the values array for this column
        const values: (string | number | boolean)[][] = [];

        if (noChangeIfEmpty) {
          range.load("values");
          await context.sync();
        }

        for (let r = minRow; r <= maxRow; r++) {
          const article = articleByRow.get(r);
          if (article) {
            const value = getFieldValue(article, field as EnrichmentField);
            if (value === undefined && noChangeIfEmpty) {
              // Keep existing cell value — read from already-loaded range
              const existingValues = range.values;
              values.push([existingValues[r - minRow][0]]);
            } else {
              values.push([value ?? ""]);
            }
          } else if (noChangeIfEmpty) {
            // Row not in results — preserve existing
            const existingValues = range.values;
            values.push([existingValues[r - minRow][0]]);
          } else {
            values.push([""]);
          }
        }

        range.values = values;
        range.format.verticalAlignment = Excel.VerticalAlignment.center;
        range.format.autofitColumns();
      }

      await context.sync();
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to write enrichment data to Excel: ${message}`);
  }
}

function getFieldValue(article: ArticleResult, field: EnrichmentField): string | undefined {
  switch (field) {
    case EnrichmentField.DescriptionInDutch:
      return article.descriptionInDutch;
    case EnrichmentField.DescriptionInFrench:
      return article.descriptionInFrench;
    case EnrichmentField.DescriptionInEnglish:
      return article.descriptionInEnglish;
    case EnrichmentField.DescriptionInGerman:
      return article.descriptionInGerman;
    case EnrichmentField.BrandCode:
      return article.brandCode;
    case EnrichmentField.BrandDescriptionInDutch:
      return article.brandDescriptionInDutch;
    case EnrichmentField.BrandDescriptionInFrench:
      return article.brandDescriptionInFrench;
    case EnrichmentField.CategoryCode:
      return article.categoryCode;
    case EnrichmentField.StatusName:
      return article.statusName;
    case EnrichmentField.StatusDescriptionInDutch:
      return article.statusDescriptionInDutch;
    case EnrichmentField.StatusDescriptionInFrench:
      return article.statusDescriptionInFrench;
    case EnrichmentField.ProductOwnerCode:
      return article.productOwnerCode;
    case EnrichmentField.MinimumPurchaseQuantity:
      return article.minimumPurchaseQuantity?.toString();
    case EnrichmentField.PrimarySupplierId:
      return article.primarySupplierId;
    default:
      return undefined;
  }
}
